// src/components/media/MediaRenderer.jsx
import React, { useState, useRef, useEffect } from 'react';
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  Maximize2, 
  ExternalLink,
  AlertCircle,
  Loader2,
  Download,
  Eye
} from 'lucide-react';

export const MediaRenderer = ({ mediaUrl, alt = 'Media content', className = '' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!mediaUrl) {
      setError('No media URL provided');
      setLoading(false);
      return;
    }

    detectMediaType(mediaUrl);
  }, [mediaUrl]);

  const detectMediaType = (url) => {
    setLoading(true);
    setError(null);

    try {
      const cleanUrl = url.toLowerCase().trim();
      
      // YouTube detection
      if (cleanUrl.includes('youtube.com/watch') || 
          cleanUrl.includes('youtu.be/') || 
          cleanUrl.includes('youtube.com/embed/')) {
        setMediaType('youtube');
        setLoading(false);
        return;
      }

      // PDF detection
      if (cleanUrl.includes('.pdf') || cleanUrl.includes('pdf')) {
        setMediaType('pdf');
        setLoading(false);
        return;
      }

      // Image detection
      if (cleanUrl.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)(\?|$)/)) {
        setMediaType('image');
        setLoading(false);
        return;
      }

      // Video detection
      if (cleanUrl.match(/\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv)(\?|$)/)) {
        setMediaType('video');
        setLoading(false);
        return;
      }

      // Audio detection
      if (cleanUrl.match(/\.(mp3|wav|ogg|aac|flac|m4a)(\?|$)/)) {
        setMediaType('audio');
        setLoading(false);
        return;
      }

      // Default to web embed
      setMediaType('embed');
      setLoading(false);
    } catch (err) {
      setError('Invalid media URL');
      setLoading(false);
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    let videoId = '';
    
    if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1].split('?')[0];
    } else if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v');
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('embed/')[1].split('?')[0];
    }
    
    return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
  };

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleImageError = (e) => {
    setError('Failed to load image');
  };

  const handleVideoError = (e) => {
    setError('Failed to load video');
  };

  const handleAudioError = (e) => {
    setError('Failed to load audio');
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 bg-gray-50 rounded-lg ${className}`}>
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-2" />
          <p className="text-gray-600 text-sm">Loading media...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center p-8 bg-red-50 rounded-lg border border-red-200 ${className}`}>
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-700 text-sm font-medium">Media Error</p>
          <p className="text-red-600 text-xs mt-1">{error}</p>
          <a 
            href={mediaUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 mt-3 text-xs text-blue-600 hover:text-blue-800"
          >
            <ExternalLink className="h-3 w-3" />
            Open in new tab
          </a>
        </div>
      </div>
    );
  }

  const renderMedia = () => {
    switch (mediaType) {
      case 'image':
        return (
          <div className="relative group">
            <img
              src={mediaUrl}
              alt={alt}
              onError={handleImageError}
              className="w-full h-auto rounded-lg shadow-sm hover:shadow-md transition-shadow"
              style={{ maxHeight: '70vh', objectFit: 'contain' }}
            />
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="flex gap-2">
                <button
                  onClick={toggleFullscreen}
                  className="p-2 bg-black bg-opacity-50 text-white rounded-lg hover:bg-opacity-75"
                  title="View fullscreen"
                >
                  <Maximize2 className="h-4 w-4" />
                </button>
                <a
                  href={mediaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-black bg-opacity-50 text-white rounded-lg hover:bg-opacity-75"
                  title="Open in new tab"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        );

      case 'video':
        return (
          <div className="relative">
            <video
              controls
              onError={handleVideoError}
              className="w-full rounded-lg shadow-sm"
              style={{ maxHeight: '70vh' }}
            >
              <source src={mediaUrl} />
              Your browser does not support the video tag.
            </video>
            <div className="absolute top-2 right-2">
              <button
                onClick={toggleFullscreen}
                className="p-2 bg-black bg-opacity-50 text-white rounded-lg hover:bg-opacity-75"
                title="View fullscreen"
              >
                <Maximize2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        );

      case 'audio':
        return (
          <div className="bg-gray-50 rounded-lg p-6 border">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <Volume2 className="h-8 w-8 text-blue-500" />
              </div>
              <div className="flex-1">
                <audio
                  controls
                  onError={handleAudioError}
                  className="w-full"
                >
                  <source src={mediaUrl} />
                  Your browser does not support the audio tag.
                </audio>
              </div>
            </div>
          </div>
        );

      case 'youtube':
        return (
          <div className="relative w-full" style={{ paddingBottom: '56.25%' /* 16:9 aspect ratio */ }}>
            <iframe
              src={getYouTubeEmbedUrl(mediaUrl)}
              title="YouTube video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute top-0 left-0 w-full h-full rounded-lg shadow-sm"
            />
          </div>
        );

      case 'pdf':
        return (
          <div className="border rounded-lg overflow-hidden shadow-sm">
            <div className="bg-gray-50 px-4 py-2 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">PDF Document</span>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={mediaUrl}
                  download
                  className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                >
                  <Download className="h-3 w-3" />
                  Download
                </a>
                <a
                  href={mediaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                >
                  <ExternalLink className="h-3 w-3" />
                  Open
                </a>
              </div>
            </div>
            <iframe
              src={`${mediaUrl}#toolbar=1&navpanes=0&scrollbar=1`}
              title="PDF document"
              className="w-full"
              style={{ height: '70vh', minHeight: '500px' }}
            />
          </div>
        );

      case 'embed':
      default:
        return (
          <div className="border rounded-lg overflow-hidden shadow-sm">
            <div className="bg-gray-50 px-4 py-2 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ExternalLink className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Web Content</span>
              </div>
              <a
                href={mediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
              >
                <ExternalLink className="h-3 w-3" />
                Open in new tab
              </a>
            </div>
            <iframe
              src={mediaUrl}
              title="Embedded content"
              className="w-full"
              style={{ height: '70vh', minHeight: '500px' }}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            />
          </div>
        );
    }
  };

  return (
    <div 
      ref={containerRef} 
      className={`media-container ${className} ${isFullscreen ? 'fullscreen-media' : ''}`}
    >
      {renderMedia()}
      
      <style jsx>{`
        .fullscreen-media {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          z-index: 9999 !important;
          background: black;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .fullscreen-media img,
        .fullscreen-media video {
          max-width: 100vw;
          max-height: 100vh;
          object-fit: contain;
        }
      `}</style>
    </div>
  );
};

export default MediaRenderer;